import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Collapse, colors, Link, ListItem } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink as RouterLink } from "react-router-dom";
import { useSelector } from "src/store";

const useStyles = makeStyles((theme) => ({
  item: {
    display: "block",
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: colors.common.white,
    padding: "10px 8px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
  },
  buttonLeaf: {
    // color: colors.common.white,
    padding: "10px 8px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
    fontWeight: theme.typography.fontWeightRegular,
    "&.depth-0": {
      "& $title": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    "&:hover": {
      backgroundColor: `rgba(0,0,0,0.25);`,
    },
  },
  buttonLeafAdmin: {
    color: colors.common.white,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(1),
  },
  title: {
    marginRight: "auto",
  },
  active: {
    color: `${
      theme.palette.userMode?.contrastText || theme.palette.primary.contrastText
    } !important`,
    background: `${
      theme.palette.userMode?.contrastText || theme.palette.primary.main
    } !important`,
    "& $title, & span": {
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& $icon, & span": {
      color: `${
        theme.palette.userMode?.backgroundColor ||
        theme.palette.primary.contrastText
      } !important`,
    },
  },
  activeAdmin: {
    color: `${
      theme.palette.adminMode?.backgroundColor ||
      theme.palette.primary.contrastText
    } !important`,
    background: `${
      theme.palette.adminMode?.contrastText || theme.palette.primary.main
    } !important`,
    "& $title, & span": {
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& $icon, & span": {
      color: `${
        theme.palette.adminMode?.backgroundColor ||
        theme.palette.primary.contrastText
      } !important`,
    },
  },
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  link,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);
  const { t } = useTranslation();
  const { adminMode } = useSelector((state) => state.settings);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button className={classes.button} onClick={handleToggle} style={style}>
          {Icon && <Icon className={classes.icon} size="20" />}

          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      {link ? (
        <Link
          href={href}
          target="_blank"
          variant="body1"
          className={clsx(
            classes.buttonLeaf,
            adminMode ? classes.buttonLeafAdmin : "",
            `depth-${depth}`
          )}
          style={{
            ...style,
            display: "flex",
            textDecoration: "none",
            alignItems: "center",
          }}
        >
          {Icon && <Icon className={classes.icon} size="20" />}
          <span className={classes.title}>{t(title)}</span>
          {Info && <Info />}
        </Link>
      ) : (
        <Button
          activeClassName={adminMode ? classes.activeAdmin : classes.active}
          className={clsx(
            classes.buttonLeaf,
            adminMode ? classes.buttonLeafAdmin : "",
            `depth-${depth}`
          )}
          component={RouterLink}
          style={style}
          to={href}
        >
          {Icon && <Icon className={classes.icon} size="20" />}
          <span className={classes.title}>{t(title)}</span>
          {Info && <Info />}
        </Button>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  open: false,
};

export default NavItem;
